import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'

import { H2, P, Text } from '@mxenabled/mxui'
import { ChevronRight } from '@mxenabled/mx-icons'

import { useGlobalCopyStore } from '../../../common'
import { StatusIndicator } from '../../../insights'
import { fromTimestampToMonthYear } from '../../../core/utils/DateUtil'
import { formatCurrency } from '../../../core/utils/NumberFormatting'
import { buildDynamicCopy } from '../../../core/utils/Localization'

interface BudgetDetailsTopAndMiddleSectionProps {
  startDate: number
  onViewHistory?: () => void
  totalTransaction: number
  amount: number
  spentPercentage: number
  remainingAmount: number
  iconColor?: string
  icon: React.ReactNode
}

export const BudgetDetailsTopSection = ({
  startDate,
  onViewHistory,
  totalTransaction,
  amount,
  spentPercentage,
  remainingAmount,
  iconColor,
  icon,
}: BudgetDetailsTopAndMiddleSectionProps) => {
  const { budgets: budgetsCopy } = useGlobalCopyStore()

  return (
    <React.Fragment>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottomColor: 'divider',
          borderBottomStyle: 'solid',
          borderBottomWidth: 1,
        }}
      >
        <Stack
          sx={{
            alignItems: 'center',
            flexDirection: 'row',
            gap: 12,
            justifyContent: 'space-between',
            pt: 16,
            px: 24,
          }}
        >
          <P bold={true} variant="subtitle1">
            {fromTimestampToMonthYear(startDate)}
          </P>
          {onViewHistory && (
            <Button endIcon={<ChevronRight />} onClick={onViewHistory} sx={{ height: 28 }}>
              <Text bold={true} variant="body2">
                {budgetsCopy.spending_history}
              </Text>
            </Button>
          )}
        </Stack>
        <StatusIndicator
          amount={
            <Stack sx={{ alignItems: 'baseline', flexDirection: 'row', gap: 4 }}>
              <H2>{formatCurrency(totalTransaction, '0,0.00')}</H2>
              <P color="text.secondary" variant="body2">
                /
              </P>
              <P color="text.secondary" variant="body2">
                {formatCurrency(amount, '0,0.00')}
              </P>
            </Stack>
          }
          completeColor={`${iconColor}.main`}
          icon={icon}
          label={budgetsCopy.spent_label}
          leftLabel={
            buildDynamicCopy(budgetsCopy.percent_spent, spentPercentage.toFixed()) as string
          }
          percentComplete={spentPercentage}
          rightLabel={
            buildDynamicCopy(
              budgetsCopy.balance_left,
              formatCurrency(remainingAmount, '0,0.00'),
            ) as string
          }
          sx={{
            pb: 24,
            pt: 16,
            px: 24,
          }}
        />
      </Box>
    </React.Fragment>
  )
}
